import type { Stone, CellPos } from '../types';
import { getShapeCells, getShapeMatrix } from '../data/shapes';

/**
 * 自动最优铺满算法
 *
 * 使用深度优先搜索 + 回溯法，尝试将石头以最优方式铺满机巧盘
 * 策略：优先放置大颜色/稀有石头，尝试所有旋转和位置
 */

interface PlacementResult {
    stoneId: string;
    anchorRow: number;
    anchorCol: number;
}

interface AutoFillResult {
    placements: PlacementResult[];
    /** 覆盖的格子数 */
    coveredCells: number;
    /** 总可用格子数 */
    totalCells: number;
}

/**
 * 尝试自动铺满机巧盘
 * @param stones 所有可用的石头
 * @param activeCells 当前激活的格子坐标
 * @param rows 棋盘行数
 * @param cols 棋盘列数
 * @returns 铺满结果，如果找不到有效方案则返回null
 */
export function autoFill(
    stones: Stone[],
    activeCells: CellPos[],
    rows: number,
    cols: number
): AutoFillResult | null {
    if (stones.length === 0) return null;

    // 创建占用网格
    const grid: boolean[][] = Array.from({ length: rows }, () =>
        Array.from({ length: cols }, () => false)
    );

    // 标记活动格子（非活动格子视为已占用）
    const activeSet = new Set(activeCells.map(c => `${c.row},${c.col}`));
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            if (!activeSet.has(`${r},${c}`)) {
                grid[r][c] = true; // 非活动格子标记为已占用
            }
        }
    }

    // 按颜色优先级排序（紫色 > 蓝色 > 绿色 > 白色）
    const colorPriority: Record<string, number> = {
        purple: 4,
        blue: 3,
        green: 2,
        white: 1,
    };

    const sortedStones = [...stones].sort((a, b) => {
        // 匠心石先放置
        if (a.type !== b.type) return a.type === 'craft' ? -1 : 1;
        return (colorPriority[b.color] || 0) - (colorPriority[a.color] || 0);
    });

    const placements: PlacementResult[] = [];
    const usedStoneIds = new Set<string>();

    // 为每块石头尝试所有旋转和位置
    function tryPlace(stoneIndex: number): boolean {
        if (stoneIndex >= sortedStones.length) {
            // 检查是否所有活动格子都被覆盖
            for (const cell of activeCells) {
                if (!grid[cell.row][cell.col]) return false;
            }
            return true;
        }

        const stone = sortedStones[stoneIndex];
        const maxRotations = getShapeMatrix(stone.shape, 0).length > 0 ? 4 : 1;
        const rotationsToTry = stone.shape === 'TIAN' ? 1 : maxRotations;

        for (let rot = 0; rot < rotationsToTry; rot++) {
            const cells = getShapeCells(stone.shape, rot);
            const stoneWidth = getShapeMatrix(stone.shape, rot)[0]?.length || 0;
            const stoneHeight = getShapeMatrix(stone.shape, rot).length || 0;

            // 尝试每个可能的锚点位置
            for (let r = 0; r <= rows - stoneHeight; r++) {
                for (let c = 0; c <= cols - stoneWidth; c++) {
                    // 检查是否可以放置
                    let canPlace = true;
                    for (const [dr, dc] of cells) {
                        const nr = r + dr;
                        const nc = c + dc;
                        if (nr < 0 || nr >= rows || nc < 0 || nc >= cols || grid[nr][nc]) {
                            canPlace = false;
                            break;
                        }
                    }

                    if (!canPlace) continue;

                    // 放置
                    for (const [dr, dc] of cells) {
                        grid[r + dr][c + dc] = true;
                    }

                    placements.push({
                        stoneId: stone.id,
                        anchorRow: r,
                        anchorCol: c,
                    });
                    usedStoneIds.add(stone.id);

                    // 更新石头的旋转状态（在原始stones数组中查找并更新）
                    stone.currentRotation = rot;

                    // 递归尝试下一个
                    if (tryPlace(stoneIndex + 1)) {
                        return true;
                    }

                    // 回溯
                    for (const [dr, dc] of cells) {
                        grid[r + dr][c + dc] = false;
                    }
                    placements.pop();
                    usedStoneIds.delete(stone.id);
                }
            }
        }

        // 尝试跳过当前石头（不放置）
        // 但这样可能导致覆盖不完整，先尝试强制放置所有石头
        return false;
    }

    // 尝试多种石头顺序来找到最优解
    // 先按当前排序尝试
    if (tryPlace(0)) {
        const covered = placements.reduce((sum, p) => {
            const stone = stones.find(s => s.id === p.stoneId);
            if (!stone) return sum;
            return sum + 4; // 每个石头占4格
        }, 0);
        return {
            placements,
            coveredCells: covered,
            totalCells: activeCells.length,
        };
    }

    // 如果上面的方案失败，尝试其他排列顺序
    // 由于排列组合太多，这里使用一种贪心策略：
    // 尝试不同的初始放置顺序
    const shuffledOrders = [
        // 按形状分组
        [...sortedStones].sort((a, b) => a.shape.localeCompare(b.shape)),
        // 按类型分组（先机巧石后匠心石）
        [...sortedStones].sort((a, b) => {
            if (a.type !== b.type) return a.type === 'skill' ? -1 : 1;
            return 0;
        }),
        // 反转排序
        [...sortedStones].reverse(),
    ];

    for (const order of shuffledOrders) {
        // 重置网格
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                grid[r][c] = !activeSet.has(`${r},${c}`);
            }
        }
        placements.length = 0;
        usedStoneIds.clear();

        // 用这个顺序重新尝试
        const reorderedStones = order;
        const tryWithOrder = (idx: number): boolean => {
            if (idx >= reorderedStones.length) {
                for (const cell of activeCells) {
                    if (!grid[cell.row][cell.col]) return false;
                }
                return true;
            }

            const stone = reorderedStones[idx];
            // 如果石头已经被放置（可能是匠心石），跳过
            if (usedStoneIds.has(stone.id)) return tryWithOrder(idx + 1);

            const rotationsToTry = stone.shape === 'TIAN' ? 1 : 4;

            for (let rot = 0; rot < rotationsToTry; rot++) {
                const cells = getShapeCells(stone.shape, rot);
                const stoneWidth = getShapeMatrix(stone.shape, rot)[0]?.length || 0;
                const stoneHeight = getShapeMatrix(stone.shape, rot).length || 0;

                for (let r = 0; r <= rows - stoneHeight; r++) {
                    for (let c = 0; c <= cols - stoneWidth; c++) {
                        let canPlace = true;
                        for (const [dr, dc] of cells) {
                            const nr = r + dr;
                            const nc = c + dc;
                            if (nr < 0 || nr >= rows || nc < 0 || nc >= cols || grid[nr][nc]) {
                                canPlace = false;
                                break;
                            }
                        }
                        if (!canPlace) continue;

                        for (const [dr, dc] of cells) {
                            grid[r + dr][c + dc] = true;
                        }
                        placements.push({ stoneId: stone.id, anchorRow: r, anchorCol: c });
                        usedStoneIds.add(stone.id);
                        stone.currentRotation = rot;

                        if (tryWithOrder(idx + 1)) return true;

                        for (const [dr, dc] of cells) {
                            grid[r + dr][c + dc] = false;
                        }
                        placements.pop();
                        usedStoneIds.delete(stone.id);
                    }
                }
            }
            return false;
        };

        if (tryWithOrder(0)) {
            const covered = placements.reduce((sum, p) => {
                const stone = stones.find(s => s.id === p.stoneId);
                if (!stone) return sum;
                return sum + 4;
            }, 0);
            return { placements, coveredCells: covered, totalCells: activeCells.length };
        }
    }

    return null;
}